import React, { FC, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { apiService } from 'utils/api';
import RepIssueCard from './rep-issue-card';
import { useParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';

const ALL_TABS = {
  ALL: 'All',
  RESOLVED: 'Resolved',
  UNRESOLVED: 'Unresolved'
};
type OutboundFilterType = (typeof ALL_TABS)[keyof typeof ALL_TABS];
const navStyles = {
  active: 'border-b border-b-[4px] border-b-[#E60A2B] mb-0 text-[#1A1A1A]',
  inactive: 'text-[#858585]'
};
type Props = {
  filter: {
    startDate: Date;
    endDate: Date;
  };
  activeTab: OutboundFilterType;
  setActiveTab: (tab: OutboundFilterType) => void;
};

const Issues: FC<Props> = ({ filter, activeTab, setActiveTab }) => {
  const isMounted = useRef(false);
  const { id } = useParams();
  const { activeCokitchen } = useDashboardContext();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [allIssuesCount, setAllIssuesCount] = useState(0);
  const [resolvedIssuesCount, setResolvedIssuesCount] = useState(0);
  const [unresolvedIssuesCount, setUnresolvedIssuesCount] = useState(0);
  const cokitchenId = activeCokitchen.id;
  useEffect(() => {
    getIssues(true);
  }, []);

  const getUrl = () => {
    const tab = activeTab;
    switch (tab) {
      case ALL_TABS.RESOLVED:
        return 'cs/assigned/resolved/issue';
      case ALL_TABS.UNRESOLVED:
        return 'cs/assigned/pending/issue';

      default:
        return 'cs/assigned/all/issue';
    }
  };

  const getIssues = async (isNew?: boolean) => {
    if (isNew) setLoading(true);
    const { startDate: start_date, endDate: end_date } = filter;
    const page = isNew ? 1 : pageNumber;
    const params = {
      page,
      start_date,
      end_date,
      cokitchen_id: cokitchenId
    };
    try {
      const res = await apiService.get(`${getUrl()}`, {
        params
      });
      setTotalPages(res.data?.total);
      const allIssues = isNew ? res.data?.results : [...issues, ...res.data.results];
      setIssues(allIssues);
    } finally {
      isMounted.current = true;
      setLoading(false);
    }
  };
  useEffect(() => {
    if (pageNumber > 1) getIssues();
  }, [pageNumber]);

  useEffect(() => {
    if (isMounted.current) {
      setPageNumber(1);
      setTotalPages(0);
      setHasMore(false);
      getIssues(true);
    }
  }, [filter, activeTab]);

  useEffect(() => {
    setHasMore(issues?.length < totalPages);
  }, [issues]);
  const fetchMoreData = () => {
    setPageNumber((p) => p + 1);
  };
  useEffect(() => {
    getIssuesCount();
  }, [issues]);
  const getIssuesCount = async () => {
    const { startDate: start_date, endDate: end_date } = filter;
    const params = {
      start_date,
      end_date
    };
    const res = await apiService.get(`cs/assigned/stats/issues/${id}`, {
      params
    });
    setAllIssuesCount(res.data?.all_issues);
    setResolvedIssuesCount(res.data?.resolved_issues);
    setUnresolvedIssuesCount(res.data?.pending_issues);
  };
  return (
    <div className='reps_info'>
      <div className='mt-8 bg-[#F7F8F8] w-full max-w-[715px] h-[144px] p-1 flex justify-center items-center rounded-[8px] '>
        <div className='bg-white w-full max-w-[707px] h-[136px] rounded-md flex justify-between items-center reps_info_status_container'>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>ALL ISSUES</p>
            <h1 className='font-semibold text-3xl'>{allIssuesCount}</h1>
          </div>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>RESOLVED ISSUES</p>
            <h1 className='font-semibold text-3xl'>{resolvedIssuesCount}</h1>
          </div>
          <div className='flex flex-col justify-center items-center reps_info_status_box'>
            <p className='font-medium text-[10px] text-[#6F7376]'>UNRESOLVED ISSUES</p>
            <h1 className='font-semibold text-3xl'>{unresolvedIssuesCount}</h1>
          </div>
        </div>
      </div>
      <div className='mt-6 flex justify-between items-center'>
        <p className='font-medium text-[20px]'>Issues</p>
      </div>
      <div className='mt-6 flex justify-between items-center gap-4 border-b border-gray-border'>
        <ul className='flex justify-start no-scroll-bar grow'>
          {Object.values(ALL_TABS).map((value, index) => (
            <li
              role='button'
              key={index}
              onClick={() => setActiveTab(value)}
              className={`flex mr-6 last:mr-0 cursor-pointer py-4 whitespace-nowrap
            ${value === activeTab ? navStyles.active : navStyles.inactive}`}>
              {value}
            </li>
          ))}
        </ul>
      </div>
      <div className='flex gap-8 flex-wrap mt-8'>
        {loading ? (
          <div className='text-2xl leading-[40vh] text-center w-[80vw]'>Loading...</div>
        ) : issues?.length === 0 ? (
          <div className='text-2xl leading-[40vh] text-center w-[80vw]'>
            No {activeTab === ALL_TABS.ALL ? null : activeTab.toLocaleLowerCase()} issues found
          </div>
        ) : (
          <InfiniteScroll
            dataLength={issues?.length ?? 0}
            next={() => fetchMoreData()}
            hasMore={hasMore}
            loader={<p className='text-center mt-8'>Loading more issues...</p>}
            endMessage={<p className='text-center mt-8'>End of list</p>}>
            <div className='flex gap-8 flex-wrap w-full justify-between'>
              {issues?.map((issue: any) => (
                <div className='w-full md:w-[47%]' key={issue.id}>
                  <RepIssueCard issue={issue} />
                </div>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default Issues;
